import classNames from 'classnames';
import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { AirtableStatus } from 'gatsby-theme-hem-store/src/constants';
import { useHasMounted } from 'hem-hooks';
import isObject from 'lodash/isObject';
import React, { Suspense, useEffect, useState } from 'react';

import { StarRating } from '@/components/StarRating';

const ReviewLightbox = React.lazy(() => import('./lightbox'));

type Props = {
  reviews: Queries.ProductReviewFragment[];
};

export const Reviews: React.FC<Props> = ({ reviews }) => {
  const hasMounted = useHasMounted();
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 4;

  // Logic for pagination
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  useEffect(() => {
    if (hasMounted) {
      const reviews: HTMLDivElement = document.querySelector('#reviews');
      const navbar: HTMLDivElement = document.querySelector('#navbar');

      if (reviews && navbar) {
        console.log(reviews, navbar);

        window.scrollTo({
          top: reviews.offsetTop - (navbar.clientHeight + 16),
          behavior: 'smooth',
        });
      }
    }
    // We do NOT want to run this effect when mounted changes, only when currentPage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  function handlePreviousPage() {
    setCurrentPage((prevPage) => prevPage - 1);
  }

  function handleNextPage() {
    setCurrentPage((prevPage) => prevPage + 1);
  }

  function handleFirstPage() {
    setCurrentPage(1);
  }

  function handleLastPage() {
    setCurrentPage(totalPages);
  }

  return (
    <section className="mx-4 mb-16 md:mx-8" id="reviews">
      <h3 className="border-b pb-4 text-xl">
        Reviews <span className="text-black-400">({reviews.length})</span>
      </h3>

      <ul className="space-y-4 divide-y" role="list">
        {React.Children.toArray(
          currentReviews.map((review) => {
            return (
              <li className="grid-cols-12 gap-2 pt-4 md:grid md:gap-4">
                <div className="col-span-3">
                  <div className="grid grid-cols-[auto_1fr] gap-2 md:block">
                    <p className="mb-4 text-base font-bold">
                      {review.name}
                      <span className="mt-1 block text-sm font-normal text-black-600">
                        {review.yotpoCreatedAt}
                        {review.verifiedBuyer ? (
                          <>
                            <br />
                            Verified buyer
                          </>
                        ) : null}
                      </span>
                    </p>

                    {!!review?.variant?.productName && (
                      <>
                        {review.variant?.airtable?.status ===
                        AirtableStatus.INACTIVE ? (
                          <div className="mb-4 flex flex-row-reverse items-start gap-2 text-right md:flex-row md:items-center md:text-left">
                            {review?.variant?.thumbnailImage
                              ?.gatsbyImageData ? (
                              <GatsbyImage
                                alt=""
                                image={
                                  review.variant.thumbnailImage.gatsbyImageData
                                }
                              />
                            ) : null}

                            <p className="text-sm font-bold text-black">
                              {review.variant.productName}{' '}
                              <span className="block font-normal">
                                {review.variant.variantName}{' '}
                                <span className="block text-black-400">
                                  (Discontinued)
                                </span>
                              </span>
                            </p>
                          </div>
                        ) : (
                          <Link
                            to={review.variant?.slug}
                            aria-label={`${review.variant?.productName} ${review.variant?.variantName}`}
                            className="mb-4 flex flex-row-reverse items-start gap-2 text-right text-sm font-bold text-black no-underline hover:underline md:flex-row md:items-center md:text-left"
                          >
                            {review?.variant?.thumbnailImage
                              ?.gatsbyImageData ? (
                              <GatsbyImage
                                alt=""
                                image={
                                  review.variant.thumbnailImage.gatsbyImageData
                                }
                              />
                            ) : null}
                            <span>
                              {review.variant?.productName}{' '}
                              <span className="block font-normal">
                                {review.variant?.variantName}
                              </span>
                            </span>
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="col-span-9">
                  <StarRating
                    scores={[review.score]}
                    showNumberOfReviews={false}
                  />

                  <p
                    className="mb-1 mt-4 max-w-prose text-pretty text-base font-bold text-black"
                    dangerouslySetInnerHTML={{ __html: review.title }}
                  />

                  {review.content.childMarkdownRemark.rawMarkdownBody !==
                  review.title ? (
                    <p
                      className="mb-4 max-w-prose text-pretty text-base font-normal text-black-600"
                      dangerouslySetInnerHTML={{
                        __html:
                          review.content.childMarkdownRemark.rawMarkdownBody,
                      }}
                    />
                  ) : null}

                  {(review.media || []).length > 0 ? (
                    <div className="mb-4">
                      <Suspense
                        fallback={
                          <div className="aspect-[3/4] h-16 animate-pulse overflow-hidden rounded-sm bg-gray-100" />
                        }
                      >
                        <ReviewLightbox
                          media={review.media}
                          fullMedia={review.fullMedia}
                        />
                      </Suspense>
                    </div>
                  ) : null}

                  {isObject(review.comment) ? (
                    <div className="border-l-4 pl-4">
                      <p className="mb-1 text-base font-bold">
                        Hem{' '}
                        <span className="block text-sm font-normal text-black-600">
                          {review.comment.createdAt}
                        </span>
                      </p>

                      <p
                        className="mb-1 max-w-prose text-pretty text-base text-black-600"
                        dangerouslySetInnerHTML={{
                          __html: review.comment.content,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            );
          }),
        )}
      </ul>

      {reviews.length > reviewsPerPage && (
        <>
          <div className="ais-Pagination">
            <div className="ais-Pagination">
              <ul className="ais-Pagination-list">
                {hasPreviousPage && (
                  <>
                    <li
                      className={classNames(
                        'ais-Pagination-item',
                        'ais-Pagination-item--disabled',
                        'ais-Pagination-item--firstPage',
                        {
                          'text-blue': currentPage !== 1,
                          'ais-Pagination-item--selected': currentPage === 1,
                        },
                      )}
                    >
                      <button
                        className="ais-Pagination-link"
                        disabled={currentPage === 1}
                        onClick={handleFirstPage}
                      >
                        «
                      </button>
                    </li>

                    <li
                      className={classNames(
                        'ais-Pagination-item',
                        'ais-Pagination-item--disabled',
                        'ais-Pagination-item--previousPage',
                        {
                          'text-blue': currentPage !== 1,
                          'ais-Pagination-item--selected': currentPage === 1,
                        },
                      )}
                    >
                      <button
                        className="ais-Pagination-link"
                        aria-label="Previous page"
                        disabled={currentPage === 1}
                        onClick={handlePreviousPage}
                      >
                        ‹
                      </button>
                    </li>
                  </>
                )}

                {Array.from({ length: totalPages }, (_, index) => {
                  const startPage = Math.max(currentPage - 1, 1);
                  const endPage = Math.min(startPage + 2, totalPages);

                  if (index + 1 >= startPage && index + 1 <= endPage) {
                    return (
                      <li
                        key={index}
                        className={classNames(
                          'ais-Pagination-item',
                          'ais-Pagination-item--page',
                          {
                            'ais-Pagination-item--selected':
                              currentPage === index + 1,
                          },
                        )}
                      >
                        <button
                          className={classNames('ais-Pagination-link', {
                            'text-blue': currentPage !== index + 1,
                            'ais-Pagination-link--selected':
                              currentPage === index + 1,
                          })}
                          disabled={currentPage === index + 1}
                          aria-label={`Page ${index + 1}`}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    );
                  }

                  return null;
                })}

                {hasNextPage && (
                  <>
                    <li
                      className={classNames(
                        'ais-Pagination-item',
                        'ais-Pagination-item--nextPage',
                        {
                          'text-blue': currentPage !== totalPages,
                          'ais-Pagination-item--selected':
                            currentPage === totalPages,
                        },
                      )}
                    >
                      <button
                        className="ais-Pagination-link"
                        aria-label="Next page"
                        disabled={currentPage === totalPages}
                        onClick={handleNextPage}
                      >
                        ›
                      </button>
                    </li>

                    <li
                      className={classNames(
                        'ais-Pagination-item',
                        'ais-Pagination-item--lastPage',
                        {
                          'text-blue': currentPage !== totalPages,
                          'ais-Pagination-item--selected':
                            currentPage === totalPages,
                        },
                      )}
                    >
                      <button
                        className="ais-Pagination-link"
                        disabled={currentPage === totalPages}
                        aria-label="Last page"
                        onClick={handleLastPage}
                      >
                        »
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <p className="text-center text-base text-black-400">
            Page {currentPage} of {totalPages}
            <br />
            {reviews.length} reviews
          </p>
        </>
      )}
    </section>
  );
};

export const query = graphql`
  fragment ProductReview on ContentfulYotpoProductReview {
    __typename
    score
    name
    title
    content {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
    verifiedBuyer
    yotpoCreatedAt(formatString: "LL")
    media {
      gatsbyImageData(
        formats: [AVIF, WEBP, AUTO]
        height: 64
        placeholder: BLURRED
        quality: 80
      )
    }
    fullMedia: media {
      gatsbyImageData(
        formats: [AVIF, WEBP, AUTO]
        placeholder: BLURRED
        quality: 80
      )
    }
    comment {
      createdAt(formatString: "LL")
      content
    }
    variant {
      slug
      productName
      variantName
      thumbnailImage {
        gatsbyImageData(
          formats: [AVIF, WEBP, AUTO]
          quality: 80
          width: 40
          layout: FIXED
          placeholder: DOMINANT_COLOR
          resizingBehavior: THUMB
        )
      }
      airtable {
        status
        product {
          configuration
        }
      }
    }
  }
`;
